<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAnimalStore } from '@/stores/animal'
import { useAuthStore } from '@/stores/auth'
import { animalService } from '@/api/animal'
import MortalityForm from './MortalityForm.vue'
import HealthCheckForm from './HealthCheckForm.vue'
import { healthService, type HealthEvent, type Vaccination, type Deworming, type HealthCheck } from '@/api/health'
import HealthEventForm from './HealthEventForm.vue'
import TreatmentForm from './TreatmentForm.vue'
import VaccinationForm from './VaccinationForm.vue'
import DewormingForm from './DewormingForm.vue'
import QuarantineForm from './QuarantineForm.vue'

interface Animal {
  id?: string
  species: string
  breed?: string
  gender: string
  health_status: string
  location: string
  count: number
  owner_name?: string
  created_by_name?: string
}

const props = defineProps<{
  animal: Animal
}>()

const emit = defineEmits<{
  close: []
}>()

const animalStore = useAnimalStore()
const authStore = useAuthStore()

const selectedAnimal = ref(props.animal)
const activeTab = ref('overview')
const loading = ref(false)
const healthEvents = ref<HealthEvent[]>([])
const vaccinations = ref<Vaccination[]>([])
const dewormingRecords = ref<Deworming[]>([])
const healthChecks = ref<HealthCheck[]>([])
const healthAlerts = ref<any[]>([])

// Modal states
const showHealthEventModal = ref(false)
const showTreatmentModal = ref(false)
const showVaccinationModal = ref(false)
const showDewormingModal = ref(false)
const showQuarantineModal = ref(false)
const showMortalityModal = ref(false)
const showHealthCheckModal = ref(false)

const selectedHealthEvent = ref<HealthEvent | null>(null)

onMounted(async () => {
  await fetchAnimalHealthData()
})

async function fetchAnimalHealthData() {
  if (!selectedAnimal.value) return
  
  loading.value = true
  try {
    // Fetch health events using health service
    const healthResponse = await healthService.getHealthEvents(selectedAnimal.value.id)
    const healthData = healthResponse.data as any
    healthEvents.value = Array.isArray(healthData) ? healthData : (healthData.results || [])

    // Fetch vaccinations using health service
    const vaccResponse = await healthService.getVaccinations(selectedAnimal.value.id)
    const vaccData = vaccResponse.data as any
    vaccinations.value = Array.isArray(vaccData) ? vaccData : (vaccData.results || [])

    // Fetch deworming records using health service
    const dewormResponse = await healthService.getDewormingRecords(selectedAnimal.value.id)
    const dewormData = dewormResponse.data as any
    dewormingRecords.value = Array.isArray(dewormData) ? dewormData : (dewormData.results || [])

    // Fetch health checks using health service
    const checkResponse = await healthService.getHealthChecks(selectedAnimal.value.id)
    const checkData = checkResponse.data as any
    healthChecks.value = Array.isArray(checkData) ? checkData : (checkData.results || [])

    // Note: Health alerts endpoint doesn't exist yet - leaving empty for now
    healthAlerts.value = []
  } catch (err) {
    console.error('Failed to fetch health data:', err)
    // Set fallback values on error
    healthEvents.value = []
    vaccinations.value = []
    dewormingRecords.value = []
    healthAlerts.value = []
  } finally {
    loading.value = false
  }
}

function openHealthEventModal(healthEvent?: HealthEvent) {
  selectedHealthEvent.value = healthEvent || null
  showHealthEventModal.value = true
}

function openTreatmentModal(healthEvent: HealthEvent) {
  selectedHealthEvent.value = healthEvent
  showTreatmentModal.value = true
}

function openVaccinationModal() {
  showVaccinationModal.value = true
}

function openDewormingModal() {
  showDewormingModal.value = true
}

function openQuarantineModal() {
  showQuarantineModal.value = true
}

function openMortalityModal() {
  showMortalityModal.value = true
}

function openHealthCheckModal() {
  showHealthCheckModal.value = true
}

function closeModal(modalType: string) {
  switch (modalType) {
    case 'healthEvent':
      showHealthEventModal.value = false
      break
    case 'treatment':
      showTreatmentModal.value = false
      break
    case 'vaccination':
      showVaccinationModal.value = false
      break
    case 'deworming':
      showDewormingModal.value = false
      break
    case 'quarantine':
      showQuarantineModal.value = false
      break
    case 'mortality':
      showMortalityModal.value = false
      break
    case 'healthCheck':
      showHealthCheckModal.value = false
      break
  }
  selectedHealthEvent.value = null
  fetchAnimalHealthData()
}

function getSeverityColor(severity: string) {
  const colors = {
    'Mild': 'bg-green-100 text-green-800',
    'Moderate': 'bg-yellow-100 text-yellow-800',
    'Severe': 'bg-orange-100 text-orange-800',
    'Critical': 'bg-red-100 text-red-800'
  }
  return colors[severity as keyof typeof colors] || 'bg-gray-100 text-gray-800'
}

function getStatusColor(status: string) {
  const colors = {
    'Ongoing': 'bg-red-100 text-red-800',
    'Recovered': 'bg-green-100 text-green-800',
    'Chronic': 'bg-yellow-100 text-yellow-800',
    'Died': 'bg-gray-100 text-gray-800'
  }
  return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
}

function getHealthStatusColor(status: string) {
  const colors = {
    'Healthy': 'bg-green-100 text-green-800',
    'Sick': 'bg-red-100 text-red-800',
    'Injured': 'bg-orange-100 text-orange-800',
    'Quarantined': 'bg-yellow-100 text-yellow-800',
    'Deceased': 'bg-gray-100 text-gray-800'
  }
  return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
}

const activeHealthEvents = computed(() => 
  healthEvents.value.filter(event => event.status === 'Ongoing')
)

const overdueVaccinations = computed(() => 
  vaccinations.value.filter(vacc => vacc.is_overdue)
)

const overdueDeworming = computed(() => 
  dewormingRecords.value.filter(deworm => deworm.is_overdue)
)

const activeWithdrawalPeriods = computed(() => {
  let count = 0
  healthEvents.value.forEach(event => {
    (event.treatments || []).forEach(treatment => {
      if (treatment.is_withdrawal_active) count++
    })
  })
  return count
})
</script>

<template>
  <div class="min-h-screen bg-gray-50/50 p-6">
    <div class="max-w-7xl mx-auto space-y-8">
      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-lg p-8 text-white">
        <h1 class="text-3xl font-extrabold tracking-tight">Health Management</h1>
        <p class="mt-2 text-blue-50 text-lg max-w-2xl">
          Professional livestock health monitoring and treatment tracking system.
        </p>
      </div>

      <!-- Animal Info -->
      <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">{{ selectedAnimal.species }}</h2>
            <p class="text-sm text-gray-500">{{ selectedAnimal.breed || 'Unknown breed' }} • {{ selectedAnimal.gender }}</p>
          </div>
          <span :class="['px-3 py-1 rounded-full text-sm font-medium', getHealthStatusColor(selectedAnimal.health_status)]">
            {{ selectedAnimal.health_status }}
          </span>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <!-- Health Summary Sidebar -->
        <div class="lg:col-span-1 space-y-6">
          <!-- Quick Stats -->
          <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">Health Summary</h3>
            
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Active Issues</span>
                <span class="font-semibold text-red-600">{{ activeHealthEvents.length }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Overdue Vaccines</span>
                <span class="font-semibold text-orange-600">{{ overdueVaccinations.length }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Overdue Deworming</span>
                <span class="font-semibold text-yellow-600">{{ overdueDeworming.length }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">Withdrawal Periods</span>
                <span class="font-semibold text-purple-600">{{ activeWithdrawalPeriods }}</span>
              </div>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            
            <div class="space-y-3">
              <button
                @click="openHealthEventModal()"
                class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Record Health Event
              </button>
              <button
                @click="openVaccinationModal()"
                class="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                Add Vaccination
              </button>
              <button
                @click="openDewormingModal()"
                class="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                Record Deworming
              </button>
              <button
                @click="openQuarantineModal()"
                class="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
              >
                Start Quarantine
              </button>
              <button
                @click="openMortalityModal()"
                class="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Record Mortality
              </button>
              <button
                @click="openHealthCheckModal()"
                class="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
              >
                Health Check
              </button>
            </div>
          </div>

          <!-- Health Alerts -->
          <div v-if="healthAlerts.length > 0" class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">Health Alerts</h3>
            
            <div class="space-y-3">
              <div
                v-for="alert in healthAlerts.slice(0, 5)"
                :key="alert.created_at"
                class="p-3 rounded-lg border"
                :class="{
                  'border-red-200 bg-red-50': alert.severity === 'high',
                  'border-yellow-200 bg-yellow-50': alert.severity === 'medium',
                  'border-blue-200 bg-blue-50': alert.severity === 'low'
                }"
              >
                <div class="text-sm font-medium">{{ alert.message }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ alert.type }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="lg:col-span-3">
          <!-- Tabs -->
          <div class="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div class="border-b border-gray-200">
              <nav class="flex space-x-8 px-6" aria-label="Tabs">
                <button
                  v-for="tab in ['overview', 'health-events', 'vaccinations', 'deworming', 'checks']"
                  :key="tab"
                  @click="activeTab = tab"
                  :class="[
                    'py-4 px-1 border-b-2 font-medium text-sm capitalize',
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  ]"
                >
                  {{ tab.replace('-', ' ') }}
                </button>
              </nav>
            </div>

            <div class="p-6">
              <!-- Overview Tab -->
              <div v-if="activeTab === 'overview'" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Recent Health Events -->
                  <div>
                    <h3 class="text-lg font-semibold text-gray-800 mb-4">Recent Health Events</h3>
                    <div v-if="healthEvents.length === 0" class="text-gray-500 text-center py-8">
                      No health events recorded
                    </div>
                    <div v-else class="space-y-3">
                      <div
                        v-for="event in healthEvents.slice(0, 5)"
                        :key="event.id"
                        class="p-4 border border-gray-200 rounded-lg"
                      >
                        <div class="flex justify-between items-start">
                          <div>
                            <div class="font-medium text-gray-900">{{ event.condition }}</div>
                            <div class="text-sm text-gray-500">{{ event.start_date }}</div>
                          </div>
                          <div class="flex space-x-2">
                            <span :class="['px-2 py-1 rounded-full text-xs font-medium', getSeverityColor(event.severity)]">
                              {{ event.severity }}
                            </span>
                            <span :class="['px-2 py-1 rounded-full text-xs font-medium', getStatusColor(event.status)]">
                              {{ event.status }}
                            </span>
                          </div>
                        </div>
                        
                        <div v-if="event.symptoms && event.symptoms.length > 0" class="mt-3">
                          <div class="text-sm text-gray-600">Symptoms:</div>
                          <div class="flex flex-wrap gap-1 mt-1">
                            <span
                              v-for="symptom in event.symptoms"
                              :key="symptom.observation_date"
                              class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                            >
                              {{ symptom.symptom_name }}
                            </span>
                          </div>
                        </div>
                        
                        <div class="mt-3 flex space-x-2">
                          <button
                            @click="openTreatmentModal(event)"
                            class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Add Treatment
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Upcoming Care -->
                  <div>
                    <h3 class="text-lg font-semibold text-gray-800 mb-4">Upcoming Care</h3>
                    <div class="space-y-3">
                      <!-- Overdue Vaccinations -->
                      <div v-if="overdueVaccinations.length > 0">
                        <div class="text-sm font-medium text-red-600 mb-2">Overdue Vaccinations</div>
                        <div
                          v-for="vacc in overdueVaccinations"
                          :key="vacc.id"
                          class="p-3 border border-red-200 bg-red-50 rounded-lg"
                        >
                          <div class="font-medium text-red-800">{{ vacc.vaccine_name }}</div>
                          <div class="text-sm text-red-600">Overdue</div>
                        </div>
                      </div>

                      <!-- Upcoming Vaccinations -->
                      <div v-if="vaccinations.filter(v => !v.is_overdue && v.next_due_date).length > 0">
                        <div class="text-sm font-medium text-blue-600 mb-2">Upcoming Vaccinations</div>
                        <div
                          v-for="vacc in vaccinations.filter(v => !v.is_overdue && v.next_due_date).slice(0, 3)"
                          :key="vacc.id"
                          class="p-3 border border-blue-200 bg-blue-50 rounded-lg"
                        >
                          <div class="font-medium text-blue-800">{{ vacc.vaccine_name }}</div>
                          <div class="text-sm text-blue-600">Due in {{ vacc.days_until_due }} days</div>
                        </div>
                      </div>

                      <!-- Overdue Deworming -->
                      <div v-if="overdueDeworming.length > 0">
                        <div class="text-sm font-medium text-yellow-600 mb-2">Overdue Deworming</div>
                        <div
                          v-for="deworm in overdueDeworming"
                          :key="deworm.id"
                          class="p-3 border border-yellow-200 bg-yellow-50 rounded-lg"
                        >
                          <div class="font-medium text-yellow-800">{{ deworm.product }}</div>
                          <div class="text-sm text-yellow-600">Overdue</div>
                        </div>
                      </div>

                      <!-- Recent Health Check -->
                      <div v-if="healthChecks.length > 0">
                        <div class="text-sm font-medium text-amber-600 mb-2">Last Health Check</div>
                        <div class="p-3 border border-amber-200 bg-amber-50 rounded-lg">
                          <div class="font-medium text-amber-800">{{ healthChecks[0].check_type }} Check</div>
                          <div class="text-sm text-amber-600">{{ new Date(healthChecks[0].check_date).toLocaleDateString() }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Health Events Tab -->
              <div v-if="activeTab === 'health-events'" class="space-y-4">
                <div class="flex justify-between items-center">
                  <h3 class="text-lg font-semibold text-gray-800">Health Events</h3>
                  <button
                    @click="openHealthEventModal()"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Add Health Event
                  </button>
                </div>
                
                <div v-if="healthEvents.length === 0" class="text-gray-500 text-center py-8">
                  No health events recorded
                </div>
                
                <div v-else class="space-y-4">
                  <div
                    v-for="event in healthEvents"
                    :key="event.id"
                    class="p-4 border border-gray-200 rounded-lg"
                  >
                    <div class="flex justify-between items-start">
                      <div>
                        <div class="font-medium text-gray-900">{{ event.condition }}</div>
                        <div class="text-sm text-gray-500">
                          {{ event.start_date }} - {{ event.end_date || 'Ongoing' }}
                        </div>
                      </div>
                      <div class="flex space-x-2">
                        <span :class="['px-2 py-1 rounded-full text-xs font-medium', getSeverityColor(event.severity)]">
                          {{ event.severity }}
                        </span>
                        <span :class="['px-2 py-1 rounded-full text-xs font-medium', getStatusColor(event.status)]">
                          {{ event.status }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Symptoms and treatments details would go here -->
                  </div>
                </div>
              </div>

              <!-- Other tabs content would go here -->
              <!-- Other tabs content -->
              <div v-else-if="activeTab === 'vaccinations'" class="space-y-4">
                <div class="flex justify-between items-center">
                  <h3 class="text-lg font-semibold text-gray-800">Vaccination History</h3>
                  <button @click="openVaccinationModal()" class="text-blue-600 text-sm font-medium">Add Record</button>
                </div>
                <div v-if="vaccinations.length === 0" class="text-gray-500 text-center py-8">
                  No vaccinations recorded
                </div>
                <div v-else class="divide-y divide-gray-100">
                  <div v-for="vacc in vaccinations" :key="vacc.id" class="py-4">
                    <div class="flex justify-between items-start">
                      <div>
                        <div class="font-medium text-gray-900">{{ vacc.vaccine_name }}</div>
                        <div class="text-sm text-gray-500">Administered: {{ vacc.vaccination_date }}</div>
                      </div>
                      <div v-if="vacc.next_due_date" class="text-right">
                        <div class="text-xs text-gray-400">Next Due</div>
                        <div :class="['text-sm font-medium', vacc.is_overdue ? 'text-red-600' : 'text-gray-900']">
                          {{ vacc.next_due_date }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else-if="activeTab === 'deworming'" class="space-y-4">
                <div class="flex justify-between items-center">
                  <h3 class="text-lg font-semibold text-gray-800">Deworming Records</h3>
                  <button @click="openDewormingModal()" class="text-blue-600 text-sm font-medium">Add Record</button>
                </div>
                <div v-if="dewormingRecords.length === 0" class="text-gray-500 text-center py-8">
                  No deworming records found
                </div>
                <div v-else class="divide-y divide-gray-100">
                  <div v-for="deworm in dewormingRecords" :key="deworm.id" class="py-4">
                    <div class="flex justify-between items-start">
                      <div>
                        <div class="font-medium text-gray-900">{{ deworm.product }}</div>
                        <div class="text-sm text-gray-500">
                          {{ deworm.date_administered }} • {{ deworm.dosage || 'Standard' }} dosage
                        </div>
                      </div>
                      <div v-if="deworm.next_due_date" class="text-right">
                        <div class="text-xs text-gray-400">Next Scheduled</div>
                        <div class="text-sm font-medium text-gray-900">{{ deworm.next_due_date }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else-if="activeTab === 'checks'" class="space-y-4">
                <div class="flex justify-between items-center">
                  <h3 class="text-lg font-semibold text-gray-800">Health Examinations</h3>
                  <button @click="openHealthCheckModal()" class="text-blue-600 text-sm font-medium">New Check</button>
                </div>
                <div v-if="healthChecks.length === 0" class="text-gray-500 text-center py-8">
                  No health checks recorded
                </div>
                <div v-else class="divide-y divide-gray-100">
                  <div v-for="check in healthChecks" :key="check.id" class="py-4">
                    <div class="flex justify-between items-start mb-2">
                      <div>
                        <div class="font-medium text-gray-900">{{ check.check_type }} Examination</div>
                        <div class="text-sm text-gray-500">{{ check.check_date }} • Dr. {{ check.veterinarian || 'Unknown' }}</div>
                      </div>
                    </div>
                    <p class="text-sm text-gray-600 italic">"{{ check.findings }}"</p>
                    <div v-if="check.temperature || check.weight" class="flex gap-4 mt-2">
                       <span v-if="check.temperature" class="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">Temp: {{ check.temperature }}°C</span>
                       <span v-if="check.weight" class="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">Weight: {{ check.weight }}kg</span>
                       <span v-if="check.body_condition_score" class="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">BCS: {{ check.body_condition_score }}/9</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Health Event Modal -->
    <div
      v-if="showHealthEventModal && selectedAnimal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeModal('healthEvent')"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white">
              {{ selectedHealthEvent ? 'Edit Health Event' : 'Record Health Event' }}
            </h3>
            <button @click="closeModal('healthEvent')" class="text-white hover:text-blue-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="px-6 py-6">
            <HealthEventForm 
              :animal="selectedAnimal as any"
              :health-event="selectedHealthEvent"
              @success="closeModal('healthEvent')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Vaccination Modal -->
    <div
      v-if="showVaccinationModal && selectedAnimal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeModal('vaccination')"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white">Add Vaccination</h3>
            <button @click="closeModal('vaccination')" class="text-white hover:text-green-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="px-6 py-6">
            <VaccinationForm 
              :animal="selectedAnimal as any"
              @success="closeModal('vaccination')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Deworming Modal -->
    <div
      v-if="showDewormingModal && selectedAnimal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeModal('deworming')"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white">Record Deworming</h3>
            <button @click="closeModal('deworming')" class="text-white hover:text-purple-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="px-6 py-6">
            <DewormingForm 
              :animal="selectedAnimal as any"
              @success="closeModal('deworming')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Quarantine Modal -->
    <div
      v-if="showQuarantineModal && selectedAnimal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeModal('quarantine')"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-gradient-to-r from-yellow-600 to-orange-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white">Start Quarantine</h3>
            <button @click="closeModal('quarantine')" class="text-white hover:text-yellow-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="px-6 py-6">
            <QuarantineForm 
              :animal="selectedAnimal as any"
              @success="closeModal('quarantine')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Mortality Modal -->
    <div
      v-if="showMortalityModal && selectedAnimal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <!-- ... (keep existing mortality modal content) ... -->
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeModal('mortality')"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-gradient-to-r from-red-600 to-rose-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white">Record Mortality</h3>
            <button @click="closeModal('mortality')" class="text-white hover:text-red-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="px-6 py-6">
            <MortalityForm 
              :animal="selectedAnimal as any"
              @success="closeModal('mortality')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Health Check Modal -->
    <div
      v-if="showHealthCheckModal && selectedAnimal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeModal('healthCheck')"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white">Record Health Examination</h3>
            <button @click="closeModal('healthCheck')" class="text-white hover:text-amber-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="px-6 py-6">
            <HealthCheckForm 
              :animal="selectedAnimal as any"
              @success="closeModal('healthCheck')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
